import { DateTime } from 'luxon';
import { SubscriptionStatus } from '@cf-shared/data-model/couch-user';
import type { CouchUserCoachData } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import type { SubscriptionProvider } from 'services/subscription-strategy';

/**
 * The pure decision behind "does this user see the note saying their coach
 * already covers the membership they are also paying for?" (#4462).
 *
 * Kept outside the component so it is unit-testable without Angular, like
 * `paddle-paused-ui-policy.ts` next door and `coach-lapse-notice-policy.ts`
 * beside the Subscribe page.
 *
 * ## Why this note exists
 *
 * Before #3897, accepting a coach's invitation SKIPPED writing the coach grant
 * when the client already had paid access of their own. That was the bug: those
 * clients hit the paywall the day their own subscription ran out. #3897 removed
 * the skip, so a claim now always records the grant — and a client can hold both
 * a live coach grant and a live personal subscription at the same time.
 *
 * Holding both is correct. The coach grant is what keeps the person running when
 * their own subscription ends. But nothing in the app ever mentioned it, so they
 * could keep paying for something their coach already covers, month after month,
 * with no signal to stop. That is what this note says out loud.
 *
 * Full reasoning in design-docs/coach-subscription-lapse.md, "Surface C".
 */
export interface CoachCoversMembershipState {
  /**
   * The coach-granted block from the login result (`auth.couch?.coachData`), or
   * undefined when the user never had a coach — or when `auth.couch` is null,
   * which is the offline boot.
   */
  coachData: CouchUserCoachData | undefined;
  /**
   * The provider of the user's OWN subscription, as the SERVER reported it
   * (`subManager.provider`, from `getSubscriptionInfo`). Null when they have
   * nothing of their own, and also null before that call has ever landed.
   *
   * This is deliberately the server's answer rather than anything computed
   * here. `getSubscriptionInfo` composes it through `viewOwnSubscription`, which
   * strips the coach grant and re-reconciles exactly when
   * `livePersonalBillingProvider` says a live personal subscription exists — so
   * it is already the server's answer to this note's own question. It is also
   * the same value the Cancel / Manage / "managed via the App Store" controls on
   * this page key off, which means the note can never contradict the buttons
   * printed beside it.
   *
   * It does NOT go back to null when a later refresh fails. It is a field on a
   * singleton service, assigned only after a successful call, so a second visit
   * whose refresh failed still holds the first visit's answer. Keeping a stale
   * answer off the screen is the page's job, not this rule's — see the
   * `subscriptionInfoError` gate in `showCoachCoversMembershipNote`, and the
   * one failure path that gate deliberately does not cover.
   */
  ownSubscriptionProvider: SubscriptionProvider | null;
  /**
   * The status of that same own subscription (`subManager.subscriptionStatus`).
   * Null when the server has not answered.
   *
   * Needed because `ownSubscriptionProvider` alone does not mean "being billed".
   * `livePersonalBillingProvider` answers a deliberately wider question — "would
   * a second purchase double-bill this person?" — and so counts a PAUSED
   * subscription as live. See the `Paused` term in the rule below.
   */
  ownSubscriptionStatus: SubscriptionStatus | null;
  /** Evaluation time, passed in so the rule stays pure and testable. */
  now: DateTime;
}

/**
 * Whether to show the "your coach already covers this" note.
 *
 * Every term is a requirement, written as an explicit conjunction so a future
 * edit has to decide about each one rather than silently drop it.
 *
 * The "currently granting" term reuses `isInGrace` — the ONE access predicate
 * this codebase has, shared with #4361's coach mirror — rather than comparing
 * dates here. That matters more than it looks: the existing client population
 * still holds the literal string `'never'` as an expiry, which no naive date
 * comparison handles, and `isInGrace` is where that is already understood.
 *
 * ## Why there is no `coach-relationship/as-client` read here
 *
 * The Subscribe page's sibling rule (`shouldShowCoachLapseNotice`) needs one,
 * because its message claims the reader is STILL CONNECTED to their coach and
 * `coachDetachClient` leaves a detached client's block behind. This note claims
 * something narrower and self-evidencing: that the coach's plan is covering the
 * membership right now. A block that is currently granting is literally what
 * provides that access, so the grant test IS the claim. And both detach paths
 * set the block to `Terminated` (`coachDetachClient`, `coachClientSelfDetach`),
 * which `isInGrace` already rejects — so the detached client this rule must not
 * speak to is excluded by the same term, with no extra document read on a page
 * that would otherwise need none.
 *
 * ## What the note deliberately does NOT say
 *
 * It states the two facts and stops: the coach's plan covers you, and you also
 * pay for one yourself. No instruction to cancel, and no warning that the coach
 * could lapse. Both were considered and dropped. A cancel instruction would have
 * to be right about WHERE to cancel, which differs per provider and platform —
 * and the card already answers that with its own Cancel / Manage / off-platform
 * controls. A lapse warning would be true (a coach's own plan really can end,
 * and #4361 revokes the client at the same instant) but it turns a helpful note
 * into a hedge, and the decision is the reader's to make.
 */
export function shouldShowCoachCoversMembershipNotice(state: CoachCoversMembershipState): boolean {
  const grant = state.coachData;
  if (!grant) return false;

  const coachIsCoveringThem = isInGrace(
    grant.subscriptionStatus,
    grant.expiresDate_utc,
    state.now.toMillis(),
  );

  // A PAUSED subscription is not being billed, so "you are also paying" would be
  // a false statement about the one fact this note exists to state. It still
  // reaches here as an ordinary provider, because the server's
  // `livePersonalBillingProvider` counts a paused subscription as live on
  // purpose (it is asking whether a SECOND purchase would double-bill, which it
  // would) and the reconciler then ranks a paused block above every other
  // no-access candidate. Both of those are right for their own questions and
  // wrong for this one, which is why the status is checked separately here
  // rather than by trying to narrow what "live" means upstream.
  const theyAreBeingBilled = state.ownSubscriptionProvider !== null
    && state.ownSubscriptionStatus !== SubscriptionStatus.Paused;

  return coachIsCoveringThem && theyAreBeingBilled;
}
